const API_BASE_URL = process.env.REACT_APP_API_URL;

class FeedbackService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/feedback`;
  }

  // Get authorization header
  getAuthHeaders() {
    const token = sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  // Submit feedback
  async submitFeedback(feedbackData) {
    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(feedbackData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit feedback');
      }

      return data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  // Get user's feedback history
  async getUserFeedback() {
    try {
      const response = await fetch(`${this.baseURL}/user`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch user feedback');
      }

      return data;
    } catch (error) {
      console.error('Error fetching user feedback:', error);
      throw error;
    }
  }

  // Get all feedback (admin only)
  async getAllFeedback() {
    try {
      const response = await fetch(`${this.baseURL}/all`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch all feedback');
      }

      return data;
    } catch (error) {
      console.error('Error fetching all feedback:', error);
      throw error;
    }
  }

  // Get feedback statistics (admin only)
  async getFeedbackStats() {
    try {
      const response = await fetch(`${this.baseURL}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch feedback statistics');
      }

      return data;
    } catch (error) {
      console.error('Error fetching feedback statistics:', error);
      throw error;
    }
  }

  // Get feedback by date range (admin only)
  async getFeedbackByDateRange(startDate, endDate) {
    try {
      const response = await fetch(`${this.baseURL}/date-range?startDate=${startDate}&endDate=${endDate}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch feedback by date range');
      }

      return data;
    } catch (error) {
      console.error('Error fetching feedback by date range:', error);
      throw error;
    }
  }

  // Delete feedback (admin only)
  async deleteFeedback(feedbackId) {
    try {
      const response = await fetch(`${this.baseURL}/${feedbackId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete feedback');
      }

      return data;
    } catch (error) {
      console.error('Error deleting feedback:', error);
      throw error;
    }
  }

  // Format feedback data for display
  formatFeedbackData(feedback) {
    return {
      ...feedback,
      formattedDate: new Date(feedback.timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      ratingText: this.getRatingText(feedback.rating),
      userLocationText: this.formatLocation(feedback.userLocation)
    };
  }

  // Get rating text
  getRatingText(rating) {
    const ratingTexts = {
      1: 'Very Poor',
      2: 'Poor',
      3: 'Average',
      4: 'Good',
      5: 'Excellent'
    };
    return ratingTexts[rating] || 'Unknown';
  }

  // Format location data
  formatLocation(userLocation) {
    if (!userLocation) return 'Not provided';
    
    try {
      const location = typeof userLocation === 'string' ? JSON.parse(userLocation) : userLocation;
      if (location.latitude && location.longitude) {
        return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
      }
      return 'Invalid location data';
    } catch (error) {
      return 'Invalid location data';
    }
  }

  // Calculate average rating
  calculateAverageRating(feedbackList) {
    if (!feedbackList || feedbackList.length === 0) return 0;
    
    const totalRating = feedbackList.reduce((sum, feedback) => sum + feedback.rating, 0);
    return (totalRating / feedbackList.length).toFixed(1);
  }

  // Get rating distribution
  getRatingDistribution(feedbackList) {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    
    feedbackList.forEach(feedback => {
      if (distribution.hasOwnProperty(feedback.rating)) {
        distribution[feedback.rating]++;
      }
    });
    
    return distribution;
  }

  // Validate feedback data
  validateFeedbackData(feedbackData) {
    const errors = [];

    if (!feedbackData.rating || feedbackData.rating < 1 || feedbackData.rating > 5) {
      errors.push('Rating must be between 1 and 5');
    }

    if (feedbackData.comment && feedbackData.comment.length > 500) {
      errors.push('Comment must be 500 characters or less');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default new FeedbackService(); 